import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const CUBE_SIZE = 90;
const SHOW_DURATION = 2500;  // ms before fade-out starts
const FADE_DURATION = 600;   // ms for fade-out animation

const GameLoader = ({ onComplete }) => {
  const [fading, setFading] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!onComplete) return;
    const fadeTimer = setTimeout(() => {
      setFading(true);
      window.__LOADER_FINISHED__ = true;
      window.dispatchEvent(new CustomEvent('loaderFinished'));
    }, SHOW_DURATION);
    const doneTimer = setTimeout(() => {
      setHidden(true);
      onComplete();
    }, SHOW_DURATION + FADE_DURATION);
    return () => { clearTimeout(fadeTimer); clearTimeout(doneTimer); };
  }, [onComplete]);

  if (hidden) return null;

  return (
    <Overlay $fading={fading}>
      {/* Ambient orbs */}
      <Orb style={{ top: '18%', left: '12%', width: 420, height: 420, background: 'rgba(0,240,255,0.055)' }} />
      <Orb style={{ bottom: '18%', right: '12%', width: 380, height: 380, background: 'rgba(189,0,255,0.055)' }} />

      {/* Scanlines */}
      <Scanlines />

      <Center>

        {/* 3-D Cube */}
        <Scene>
          <Cube>
            <div className="face front" ><CubeLetter $c="#00f0ff">C</CubeLetter></div>
            <div className="face back"  ><CubeLetter $c="#bd00ff">B</CubeLetter></div>
            <div className="face left"  ><CubeLetter $c="#ff007a">E</CubeLetter></div>
            <div className="face right" ><CubeLetter $c="#00f0ff">U</CubeLetter></div>
            <div className="face top"  ><CubeDot $c="#bd00ff" /></div>
            <div className="face bottom" ><CubeTwoDots><Dot2 $c="#bd00ff" /><Dot2 $c="#ff007a" /></CubeTwoDots></div>
          </Cube>
        </Scene>

        {/* Brand — each letter blinks in sequence */}
        <Brand>
          {'CUBE GAMES'.split('').map((char, i) => (
            <BrandLetter
              key={i}
              $delay={`${i * 0.12}s`}
              $isGrad={i >= 5}
            >
              {char === ' ' ? '\u00A0' : char}
            </BrandLetter>
          ))}
        </Brand>
      </Center>
    </Overlay>
  );
};

/* ── Keyframes ── */
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const rotateCube = keyframes`
  0%   { transform: rotateX(0deg)   rotateY(0deg);   }
  25%  { transform: rotateX(90deg)  rotateY(90deg);  }
  50%  { transform: rotateX(180deg) rotateY(180deg); }
  75%  { transform: rotateX(270deg) rotateY(270deg); }
  100% { transform: rotateX(360deg) rotateY(360deg); }
`;

const barFill = keyframes`
  from { width: 0%; }
  to   { width: 100%; }
`;

const pulseDot = keyframes`
  0%, 100% { opacity: .2; transform: scale(.7);  }
  50%       { opacity: 1;  transform: scale(1.2); }
`;

const blinkCyan = keyframes`
  0%, 100% {
    opacity: 1;
    text-shadow: 0 0 20px rgba(0,240,255,0.5), 0 0 45px rgba(0,240,255,0.2);
  }
  40% {
    opacity: 0.12;
    text-shadow: none;
  }
  55% {
    opacity: 1;
    text-shadow: 0 0 30px rgba(0,240,255,1), 0 0 60px rgba(0,240,255,0.6);
  }
`;

const blinkPurple = keyframes`
  0%, 100% {
    opacity: 1;
    text-shadow: 0 0 20px rgba(189,0,255,0.5), 0 0 45px rgba(189,0,255,0.2);
  }
  40% {
    opacity: 0.12;
    text-shadow: none;
  }
  55% {
    opacity: 1;
    text-shadow: 0 0 30px rgba(189,0,255,1), 0 0 60px rgba(189,0,255,0.6);
  }
`;

const scanMove = keyframes`
  0%   { background-position: 0 0;     }
  100% { background-position: 0 100vh; }
`;

const floatCube = keyframes`
  0%, 100% { transform: translateY(0px);   }
  50%       { transform: translateY(-8px); }
`;

/* ── Styled Components ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(5, 5, 8, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  animation: ${({ $fading }) => ($fading ? fadeOut : fadeIn)} ${({ $fading }) => ($fading ? '0.6s' : '0.3s')} ease forwards;
  overflow: hidden;
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;

  @media (max-width: 600px) {
    filter: blur(60px);
    width: 240px !important;
    height: 240px !important;
  }
`;

const Scanlines = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0, 240, 255, 0.012) 0px,
    rgba(0, 240, 255, 0.012) 1px,
    transparent 1px,
    transparent 4px
  );
  animation: ${scanMove} 10s linear infinite;
`;

const Center = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;

  @media (max-width: 600px) {
    gap: 24px;
    transform: scale(0.8);
  }
`;

/* 3-D scene container gives the cube its perspective */
const Scene = styled.div`
  width: ${CUBE_SIZE}px;
  height: ${CUBE_SIZE}px;
  perspective: 700px;
  animation: ${floatCube} 3.2s ease-in-out infinite;
  filter: drop-shadow(0 0 22px rgba(0, 240, 255, 0.35))
          drop-shadow(0 0 50px rgba(189, 0, 255, 0.2));
`;

const Cube = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: ${rotateCube} 4s linear infinite;

  .face {
    position: absolute;
    width: ${CUBE_SIZE}px;
    height: ${CUBE_SIZE}px;
    border: 2px solid;
    background: transparent;
  }

  .front  {
    border-color: #00f0ff;
    transform: translateZ(${CUBE_SIZE / 2}px);
    box-shadow: inset 0 0 28px rgba(0, 240, 255, 0.12),
                      0 0 18px rgba(0, 240, 255, 0.35);
  }
  .back   {
    border-color: #bd00ff;
    transform: rotateY(180deg) translateZ(${CUBE_SIZE / 2}px);
    box-shadow: inset 0 0 28px rgba(189, 0, 255, 0.12);
  }
  .left   {
    border-color: #ff007a;
    transform: rotateY(-90deg) translateZ(${CUBE_SIZE / 2}px);
    box-shadow: inset 0 0 28px rgba(255, 0, 122, 0.12);
  }
  .right  {
    border-color: #00f0ff;
    transform: rotateY(90deg) translateZ(${CUBE_SIZE / 2}px);
    box-shadow: inset 0 0 28px rgba(0, 240, 255, 0.12);
  }
  .top    {
    border-color: #bd00ff;
    transform: rotateX(90deg) translateZ(${CUBE_SIZE / 2}px);
    box-shadow: inset 0 0 28px rgba(189, 0, 255, 0.12);
  }
  .bottom {
    border-color: #ff007a;
    transform: rotateX(-90deg) translateZ(${CUBE_SIZE / 2}px);
    box-shadow: inset 0 0 28px rgba(255, 0, 122, 0.12);
  }
`;

const CubeDot = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: '';
    display: block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${({ $c }) => $c};
    box-shadow: 0 0 10px ${({ $c }) => $c}, 0 0 22px ${({ $c }) => $c}88;
  }
`;

const CubeTwoDots = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
`;

const Dot2 = styled.span`
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${({ $c }) => $c};
  box-shadow: 0 0 10px ${({ $c }) => $c}, 0 0 22px ${({ $c }) => $c}88;
  flex-shrink: 0;
`;

const CubeLetter = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Orbitron', sans-serif;
  font-size: 38px;
  font-weight: 900;
  color: ${({ $c }) => $c};
  text-shadow: 0 0 14px ${({ $c }) => $c}, 0 0 30px ${({ $c }) => $c}66;
  letter-spacing: 0.05em;
  user-select: none;
`;

const Brand = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 30px;
  font-weight: 900;
  letter-spacing: 0.12em;
  margin: 0;
  display: flex;
`;

const BrandLetter = styled.span`
  display: inline-block;
  color: ${({ $isGrad }) => ($isGrad ? '#bd00ff' : '#00f0ff')};
  text-shadow: 0 0 18px ${({ $isGrad }) => ($isGrad ? 'rgba(189,0,255,0.7)' : 'rgba(0,240,255,0.7)')};
  animation: ${({ $isGrad }) => ($isGrad ? blinkPurple : blinkCyan)} 1.8s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay};
`;

const GradSpan = styled.span`
  background: linear-gradient(90deg, #bd00ff, #ff007a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 8px rgba(189, 0, 255, 0.55));
`;

const Sub = styled.p`
  font-family: 'Orbitron', sans-serif;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.45em;
  color: rgba(0, 240, 255, 0.38);
  margin: 0;
  text-transform: uppercase;
`;

const BarWrap = styled.div`
  width: 260px;
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 99px;
  overflow: hidden;
`;

const Bar = styled.div`
  height: 100%;
  width: 0%;
  border-radius: 99px;
  background: linear-gradient(90deg, #00f0ff, #bd00ff, #ff007a);
  box-shadow: 0 0 12px rgba(0, 240, 255, 0.6);
  animation: ${barFill} 2.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
`;

const Dots = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $c }) => $c};
  box-shadow: 0 0 6px ${({ $c }) => $c};
  animation: ${pulseDot} 1.1s ease-in-out infinite;
  animation-delay: ${({ $d }) => $d};
`;

export default GameLoader;